import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { ResultTemplateKey, type SuggestionItem } from 'types/result';
import { CheckIcon, CopyIcon } from '@radix-ui/react-icons';
import { useTranslation } from 'react-i18next';
import { Mic, MicOff } from 'lucide-react';
import { EditorContent } from '@tiptap/react';
import { useDictation } from 'hooks/useDictation';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components';
import { RichTextToolbar } from 'components/richText';
import { useNoteEditor } from './useNoteEditor';
import type { AutoSaveStatus } from 'hooks/useDebouncedFieldAutosave';
import { useResultSectionsEditorContext } from 'providers/ResultSectionsEditorProvider';
import {
  AudioBar,
  AudioLevelIndicator,
  AutoSaveIndicator,
  ContentWrapper,
  CopyButton,
  EditorScope,
  HintButton,
  HintIcon,
  MicButton,
  MicOffButton,
  SectionActions,
  SectionHeader,
  SectionHeaderLeft,
  SectionTitle,
  SectionWrapper,
} from './ResultSection.styles';

export const ResultSection = ({
  sectionKey,
  title,
  value,
  autoStatus,
  aiSuggestions,
  showSuggestionHint,
}: {
  sectionKey: ResultTemplateKey;
  title: string;
  value: string;
  autoStatus: { [key: string]: AutoSaveStatus };
  aiSuggestions: SuggestionItem[];
  showSuggestionHint?: boolean;
}) => {
  const keyString = sectionKey as string;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    editMode,
    editedText,
    toggleEditMode,
    handleTextChange,
    handleCopy,
    activeDictation,
    setActiveDictation,
  } = useResultSectionsEditorContext();

  const currentValue = editedText[keyString] ?? value ?? '';
  const hasEditor = sectionKey !== ResultTemplateKey.Diagnosis || Boolean(value);

  const { editor, insertTextAtCaret, clearPendingInsert } = useNoteEditor({
    value: currentValue,
    label: title,
    suggestions: aiSuggestions,
    onChange: (next) => handleTextChange(keyString, next),
    onFocus: () => {
      if (!editMode[keyString]) toggleEditMode(keyString);
    },
  });

  const {
    audioLevel,
    activeDictation: activeDictationKey,
    start: startDictationCore,
    initiateStop: stopDictationCore,
  } = useDictation(
    (data) => {
      insertTextAtCaret(data.text, Boolean(data.isFinal));
    },
    sectionKey,
    { onNoDevice: () => void navigate('/audio-settings') }
  );

  useEffect(() => {
    setActiveDictation(activeDictationKey);
  }, [activeDictationKey, setActiveDictation]);

  const startDictation = useCallback(async () => {
    if (!editMode[keyString]) {
      toggleEditMode(keyString);
      editor.commands.setTextSelection(editor.state.doc.content.size);
    }
    await startDictationCore();
  }, [editMode, keyString, toggleEditMode, editor, startDictationCore]);

  const stopDictation = useCallback(async () => {
    await stopDictationCore();
    clearPendingInsert();
  }, [stopDictationCore, clearPendingInsert]);

  const renderDictationButton = (disabled: boolean = false) => (
    <MicButton
      aria-label={t('dictation.start', { section: title })}
      onClick={() => {
        void startDictation();
      }}
      disabled={disabled}
    >
      <Mic className="h-5 w-5" />
    </MicButton>
  );

  return (
    <SectionWrapper>
      <SectionHeader>
        <SectionHeaderLeft>
          <SectionTitle>{title}</SectionTitle>
          {showSuggestionHint ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <HintButton aria-label={t('result.suggestions.lineBreakHint')}>
                  <HintIcon />
                </HintButton>
              </TooltipTrigger>
              <TooltipContent>{t('result.suggestions.lineBreakHint')}</TooltipContent>
            </Tooltip>
          ) : null}
        </SectionHeaderLeft>
        <SectionActions>
          {hasEditor ? (
            <>
              <RichTextToolbar editor={editor} label={title} />
              {activeDictation === sectionKey ? (
                <>
                  <AudioLevelIndicator>
                    <AudioBar style={{ height: `${Math.max(2, Math.round(audioLevel * 12))}px` }} />
                    <AudioBar style={{ height: `${Math.max(2, Math.round(audioLevel * 16))}px` }} />
                    <AudioBar style={{ height: `${Math.max(2, Math.round(audioLevel * 12))}px` }} />
                  </AudioLevelIndicator>

                  <MicOffButton
                    aria-label={t('dictation.stop', { section: title })}
                    onClick={() => {
                      void stopDictation();
                    }}
                  >
                    <MicOff className="h-5 w-5" />
                  </MicOffButton>
                </>
              ) : activeDictation === undefined ? (
                renderDictationButton()
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>{renderDictationButton(true)}</TooltipTrigger>
                  <TooltipContent>{t('result.dictation.disabled')}</TooltipContent>
                </Tooltip>
              )}
            </>
          ) : null}
          <CopyButton
            aria-label="Copy"
            onClick={() => handleCopy(keyString)}
            data-ph-no-autocapture
          >
            <CopyIcon className="h-5 w-5" />
          </CopyButton>
        </SectionActions>
      </SectionHeader>
      <ContentWrapper>
        {hasEditor ? (
          <EditorScope>
            <EditorContent editor={editor} />
          </EditorScope>
        ) : (
          <p className="text-sm text-muted-foreground">{t('result.noDiagnosisAvailable')}</p>
        )}
        {autoStatus[keyString] === 'saved' ? (
          <AutoSaveIndicator>
            <CheckIcon className="h-4 w-4" />
            <span>{t('result.autoSaveSuccessful')}</span>
          </AutoSaveIndicator>
        ) : null}
      </ContentWrapper>
    </SectionWrapper>
  );
};
